import { useEffect, useRef, useState } from 'react';
import { mediaUrl } from '../firebase/media';

type State = 'idle' | 'loading' | 'playing' | 'unavailable';

/**
 * Audio playback (§16.1).
 *
 * A speaker icon that plays on tap and never autoplays. The clip is not even
 * looked up until the rep asks for it: most turns are read, not listened to, and
 * a storage round-trip per node would cost every rep bandwidth for a thing they
 * did not use.
 *
 * The whole component is optional by construction. No path, no button. A path
 * that fails to resolve or fails to play degrades to a disabled control with a
 * plain explanation — the line is on screen either way, and §16.1 requires the
 * app to stay fully usable with audio off or broken.
 */
export function Speaker({ src, label }: { src: string | null; label: string }) {
  const [state, setState] = useState<State>('idle');
  const audio = useRef<HTMLAudioElement | null>(null);

  // A new line means the old clip is no longer what is on screen. Stop it rather
  // than letting one prospect's voice play over another's words.
  useEffect(() => {
    setState('idle');
    return () => {
      audio.current?.pause();
      audio.current = null;
    };
  }, [src]);

  if (!src) return null;

  const play = async () => {
    if (state === 'loading') return;
    if (state === 'playing') {
      audio.current?.pause();
      setState('idle');
      return;
    }
    setState('loading');
    try {
      const url = await mediaUrl(src);
      const element = new Audio(url);
      audio.current = element;
      element.addEventListener('ended', () => setState('idle'));
      element.addEventListener('error', () => setState('unavailable'));
      await element.play();
      setState('playing');
    } catch (err) {
      console.error('could not play the line', { src, err });
      setState('unavailable');
    }
  };

  if (state === 'unavailable') {
    return <span className="small muted" title="Audio is not available for this line">No audio</span>;
  }

  return (
    <button
      type="button"
      className="ghost small speaker-btn"
      aria-label={state === 'playing' ? `Stop — ${label}` : label}
      onClick={play}
      disabled={state === 'loading'}
    >
      {state === 'playing' ? '❚❚' : '▶'}
    </button>
  );
}
